import { useEffect, useState } from 'react';
import { X, Key, Zap, BarChart3, Link2 } from 'lucide-react';
import { fetchTableSchema } from '@/api/monitoring';
import type { TableSchemaResponse } from '@/api/monitoring';
import { useErrorToast, LoadRetry } from '@/lib/notify';

/* ── helpers ── */
function displayType(col: TableSchemaResponse['columns'][number]) {
  const t = col.udt_name || col.data_type;
  if (col.character_maximum_length) return `${t}(${col.character_maximum_length})`;
  if (col.numeric_precision != null && col.numeric_scale != null)
    return `${t}(${col.numeric_precision},${col.numeric_scale})`;
  return t;
}

function Badge({ text, variant }: { text: string; variant: 'blue' | 'green' | 'amber' | 'rose' | 'gray' }) {
  const cls = {
    blue:  'bg-blue-50 text-blue-700',
    green: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    rose:  'bg-rose-50 text-rose-600',
    gray:  'bg-gray-100 text-gray-500',
  }[variant];
  return <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>{text}</span>;
}

function StatRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-xs font-medium text-gray-800">{value ?? '—'}</span>
    </div>
  );
}

/* ── tabs ── */
type Tab = 'columns' | 'indexes' | 'stats' | 'constraints';

export default function TableSchemaModal({
  schema,
  table,
  onClose,
}: {
  schema: string;
  table: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<TableSchemaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [tab, setTab] = useState<Tab>('columns');

  useEffect(() => {
    fetchTableSchema(schema, table)
      .then(setData)
      .catch(e => setError(e?.response?.data?.error ?? e.message))
      .finally(() => setLoading(false));
  }, [schema, table]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const TABS: { id: Tab; label: string; count?: number }[] = [
    { id: 'columns',     label: 'Sütunlar',    count: data?.columns.length },
    { id: 'indexes',     label: 'İndeksler',   count: data?.indexes.length },
    { id: 'stats',       label: 'İstatistikler' },
    { id: 'constraints', label: 'Kısıtlamalar', count: data?.constraints.length },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              <span className="text-gray-400 font-normal">{schema}.</span>{table}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Tablo yapısı</p>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3 border-b">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors flex items-center gap-1.5 ${
                tab === t.id
                  ? 'text-blue-600 border-b-2 border-blue-500 -mb-px bg-blue-50/50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
              {t.count != null && (
                <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5 leading-none">
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Yükleniyor...</div>
          ) : error ? (
            <LoadRetry />
          ) : !data ? null : (

            <>
              {/* ── Columns ── */}
              {tab === 'columns' && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-3 py-2">Sütun</th>
                      <th className="text-left px-3 py-2">Tip</th>
                      <th className="text-center px-3 py-2 whitespace-nowrap">Nullable</th>
                      <th className="text-left px-3 py-2">Default</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.columns.map((col, i) => {
                      const isPK = data.constraints.some(
                        c => c.constraint_type === 'PRIMARY KEY' && c.column_name === col.column_name,
                      );
                      return (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-gray-800 flex items-center gap-1.5">
                            {isPK && <Key size={11} className="text-amber-500 flex-shrink-0" />}
                            {col.column_name}
                          </td>
                          <td className="px-3 py-2">
                            <Badge text={displayType(col)} variant="blue" />
                          </td>
                          <td className="px-3 py-2 text-center whitespace-nowrap w-24">
                            {col.is_nullable === 'YES'
                              ? <span className="text-xs font-medium text-gray-400">NULL</span>
                              : <Badge text="NOT NULL" variant="rose" />}
                          </td>
                          <td className="px-3 py-2">
                            {col.column_default
                              ? <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{col.column_default}</code>
                              : <span className="text-xs text-gray-300">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* ── Indexes ── */}
              {tab === 'indexes' && (
                <div className="space-y-2">
                  {data.indexes.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-10">İndeks bulunamadı</p>
                  )}
                  {[...data.indexes]
                    .sort((a, b) => (b.index_size_bytes ?? 0) - (a.index_size_bytes ?? 0))
                    .map((idx, i) => {
                    const neverUsed = idx.idx_scan === 0;
                    return (
                    <div key={i} className={`rounded-xl border p-4 ${neverUsed ? 'border-amber-200 bg-amber-50/40' : 'border-gray-100 bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Zap size={14} className={idx.is_primary ? 'text-amber-500' : 'text-blue-400'} />
                          <span className="text-sm font-medium text-gray-800">{idx.index_name}</span>
                          {idx.is_primary && <Badge text="PRIMARY" variant="amber" />}
                          {idx.is_unique && !idx.is_primary && <Badge text="UNIQUE" variant="green" />}
                          <Badge text={idx.index_type.toUpperCase()} variant="gray" />
                          {neverUsed && <Badge text="HİÇ KULLANILMADI" variant="rose" />}
                        </div>
                        <span className="text-xs font-medium text-gray-500">{idx.index_size}</span>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        <span className="font-medium">Sütunlar: </span>{idx.columns}
                      </p>
                      {idx.idx_scan != null && (
                        <div className="flex gap-4 border-t border-gray-200/60 pt-2.5">
                          <div>
                            <p className="text-xs text-gray-400">Kullanım (scan)</p>
                            <p className={`text-sm font-bold ${idx.idx_scan === 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {idx.idx_scan.toLocaleString('tr-TR')}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Okunan satır</p>
                            <p className="text-sm font-bold text-gray-700">{(idx.idx_tup_read ?? 0).toLocaleString('tr-TR')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Getirilen satır</p>
                            <p className="text-sm font-bold text-gray-700">{(idx.idx_tup_fetch ?? 0).toLocaleString('tr-TR')}</p>
                          </div>
                          {idx.idx_blks_hit != null && (() => {
                            const total = (idx.idx_blks_hit ?? 0) + (idx.idx_blks_read ?? 0);
                            const pct   = total > 0 ? Math.round((idx.idx_blks_hit / total) * 100) : null;
                            const cls   = pct == null ? 'text-gray-400' : pct >= 95 ? 'text-emerald-600' : pct >= 80 ? 'text-amber-600' : 'text-rose-600';
                            return (
                              <div>
                                <p className="text-xs text-gray-400">Cache hit %</p>
                                <p className={`text-sm font-bold ${cls}`}>{pct != null ? `${pct}%` : '—'}</p>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}

              {/* ── Stats ── */}
              {tab === 'stats' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {!data.stats ? (
                    <p className="text-sm text-gray-400 col-span-2 text-center py-10">İstatistik bulunamadı</p>
                  ) : (
                    <>
                      <div className="rounded-xl border p-4">
                        <div className="flex items-center gap-2 mb-3 text-gray-700 font-medium text-sm">
                          <BarChart3 size={15} /> Satır İstatistikleri
                        </div>
                        <StatRow label="Canlı satır"            value={Number(data.stats.n_live_tup).toLocaleString('tr-TR')} />
                        <StatRow label="Ölü satır"              value={Number(data.stats.n_dead_tup).toLocaleString('tr-TR')} />
                        <StatRow label="Analyze'den beri değişen" value={Number(data.stats.n_mod_since_analyze).toLocaleString('tr-TR')} />
                        <StatRow label="Eklenen"                value={Number(data.stats.n_tup_ins).toLocaleString('tr-TR')} />
                        <StatRow label="Güncellenen"            value={Number(data.stats.n_tup_upd).toLocaleString('tr-TR')} />
                        <StatRow label="Silinen"                value={Number(data.stats.n_tup_del).toLocaleString('tr-TR')} />
                      </div>

                      <div className="rounded-xl border p-4">
                        <div className="flex items-center gap-2 mb-3 text-gray-700 font-medium text-sm">
                          <Zap size={15} /> Tarama İstatistikleri
                        </div>
                        <StatRow label="Sequential scan"         value={Number(data.stats.seq_scan).toLocaleString('tr-TR')} />
                        <StatRow label="Sequential okunan satır" value={Number(data.stats.seq_tup_read).toLocaleString('tr-TR')} />
                        <StatRow label="Index scan"              value={Number(data.stats.idx_scan).toLocaleString('tr-TR')} />
                        <StatRow label="Index ile getirilen"     value={Number(data.stats.idx_tup_fetch).toLocaleString('tr-TR')} />
                        {(() => {
                          const total = (data.stats.seq_scan ?? 0) + (data.stats.idx_scan ?? 0);
                          const pct   = total > 0 ? Math.round((data.stats.seq_scan / total) * 100) : 0;
                          const cls   = pct >= 50 ? 'text-rose-600 font-bold' : pct >= 20 ? 'text-amber-600' : 'text-emerald-600';
                          return <StatRow label="Seq Scan oranı" value={<span className={cls}>{pct}%</span> as any} />;
                        })()}
                      </div>

                      <div className="rounded-xl border p-4">
                        <div className="flex items-center gap-2 mb-3 text-gray-700 font-medium text-sm">
                          <BarChart3 size={15} /> I/O Cache
                        </div>
                        {(() => {
                          const hTotal = (data.stats.heap_blks_hit ?? 0) + (data.stats.heap_blks_read ?? 0);
                          const hPct   = hTotal > 0 ? Math.round(((data.stats.heap_blks_hit ?? 0) / hTotal) * 100) : null;
                          const iTotal = (data.stats.idx_blks_hit ?? 0) + (data.stats.idx_blks_read ?? 0);
                          const iPct   = iTotal > 0 ? Math.round(((data.stats.idx_blks_hit ?? 0) / iTotal) * 100) : null;
                          const cls    = (v: number) => v >= 95 ? 'text-emerald-600' : v >= 80 ? 'text-amber-600' : 'text-rose-600 font-bold';
                          return <>
                            <StatRow label="Heap cache hit"    value={(data.stats.heap_blks_hit ?? 0).toLocaleString('tr-TR')} />
                            <StatRow label="Heap disk read"    value={(data.stats.heap_blks_read ?? 0).toLocaleString('tr-TR')} />
                            <StatRow label="Heap cache hit %"  value={hPct != null ? <span className={cls(hPct)}>{hPct}%</span> as any : '—'} />
                            <StatRow label="Index cache hit"   value={(data.stats.idx_blks_hit ?? 0).toLocaleString('tr-TR')} />
                            <StatRow label="Index disk read"   value={(data.stats.idx_blks_read ?? 0).toLocaleString('tr-TR')} />
                            <StatRow label="Index cache hit %" value={iPct != null ? <span className={cls(iPct)}>{iPct}%</span> as any : '—'} />
                          </>;
                        })()}
                      </div>

                      <div className="rounded-xl border p-4 sm:col-span-2">
                        <div className="flex items-center gap-2 mb-3 text-gray-700 font-medium text-sm">
                          <BarChart3 size={15} /> Bakım Geçmişi
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          {[
                            { label: 'Son Vacuum',      value: data.stats.last_vacuum },
                            { label: 'Son Auto Vacuum', value: data.stats.last_autovacuum },
                            { label: 'Son Analyze',     value: data.stats.last_analyze },
                            { label: 'Son Auto Analyze',value: data.stats.last_autoanalyze },
                          ].map(item => (
                            <div key={item.label} className="rounded-lg bg-gray-50 p-3">
                              <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                              <p className="text-xs font-medium text-gray-700">{item.value ?? '—'}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── Constraints ── */}
              {tab === 'constraints' && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-3 py-2">Kısıtlama</th>
                      <th className="text-left px-3 py-2">Tip</th>
                      <th className="text-left px-3 py-2">Sütun</th>
                      <th className="text-left px-3 py-2">Referans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.constraints.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-10 text-center text-gray-400 text-sm">Kısıtlama bulunamadı</td>
                      </tr>
                    )}
                    {data.constraints.map((c, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">{c.constraint_name}</td>
                        <td className="px-3 py-2">
                          <Badge
                            text={c.constraint_type}
                            variant={
                              c.constraint_type === 'PRIMARY KEY' ? 'amber' :
                              c.constraint_type === 'FOREIGN KEY' ? 'blue'  :
                              c.constraint_type === 'UNIQUE'      ? 'green' : 'gray'
                            }
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-800">{c.column_name}</td>
                        <td className="px-3 py-2">
                          {c.foreign_table ? (
                            <span className="flex items-center gap-1 text-xs text-blue-600">
                              <Link2 size={11} />
                              {c.foreign_table}.{c.foreign_column}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
